import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, User, Lock, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    login: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.login, formData.password);
      toast.success('Connexion réussie');
      navigate('/feed');
    } catch (error) {
      let message = 'Erreur de connexion';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = detail.map(d => d.msg || d).join(', ');
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center py-12 px-6">
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/5594992/pexels-photo-5594992.jpeg)',
        }}
      />
      
      <div className="relative w-full max-w-md">
        <div className="glass-card p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <Link to="/" className="inline-block mb-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
                <span className="text-dark font-serif text-2xl">D</span>
              </div>
            </Link>
            <h1 className="font-serif text-3xl gold-gradient-text mb-2">Connexion</h1>
            <p className="text-gray-400 text-sm">Retrouvez votre communauté exclusive</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="login" className="text-gray-400">Email ou Pseudo</Label>
              <div className="flex items-center border-b border-white/20 focus-within:border-gold transition-colors">
                <User className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                <input
                  id="login"
                  type="text"
                  placeholder="votre@email.com ou pseudo"
                  value={formData.login}
                  onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                  className="flex-1 bg-transparent border-none outline-none text-white py-3 placeholder:text-white/30"
                  required
                  data-testid="login-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-400">Mot de passe</Label>
              <div className="flex items-center border-b border-white/20 focus-within:border-gold transition-colors">
                <Lock className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="flex-1 bg-transparent border-none outline-none text-white py-3 placeholder:text-white/30"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-500 hover:text-gold transition-colors ml-3 flex-shrink-0"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-gold flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
              ) : (
                <>
                  Se connecter
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>

            <div className="text-center mt-4">
              <Link 
                to="/forgot-password" 
                className="text-sm text-gray-400 hover:text-gold transition-colors"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-400 text-sm">
              Pas encore membre ?{' '}
              <Link to="/register" className="text-gold hover:text-gold-light transition-colors" data-testid="register-link">
                Rejoindre la communauté
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
